import { sleep } from '@remote-voice/utilities';

import {
  useCallback,
  useRef,
  useEffect,
  useState,
  MutableRefObject,
  useMemo,
} from 'react';

import { AudioStream } from '@/components/organisms/chat/AudioStream';
import useConfirmOkDialog from '@/components/organisms/confirmDialog/useConfirmOkDialog';
import { env } from '@/env';
import { TtsService } from '@/types/graphql';

const maxRetry = 5;
const pingInterval = 3000;
const pongTimeOut = 2000;
// 読み上げ要求時に接続完了を待つ最大時間（音声伝達への切替直後は、接続前の待機1秒＋接続処理の時間がかかる）
const connectionWaitTimeout = 5000;

/**
 * 読み上げ用WebSocketが接続されていないため、読み上げ要求を送信できない
 */
export class TtsSocketNotConnectedError extends Error {
  constructor() {
    super('TTS WebSocket is not connected');
    this.name = 'TtsSocketNotConnectedError';
  }
}

export const useTtsWebSocket = ({
  wsSessionId,
  setSessionStreamId,
  handleResetStream,
  sessionStreamIdRef,
  playMessageEnabled,
}: {
  wsSessionId: string;
  sessionStreamIdRef: MutableRefObject<string | undefined>;
  setSessionStreamId: (id: string | undefined) => void;
  handleResetStream: () => void;
  playMessageEnabled: boolean;
}) => {
  const wsRef = useRef<WebSocket | null>(null);
  const confirmOkDialog = useConfirmOkDialog();

  const handleResetStreamRef = useRef(handleResetStream);
  const setSessionStreamIdRef = useRef(setSessionStreamId);
  const audioStream = useMemo(() => new AudioStream(), []);
  const audioStreamRef = useRef(audioStream);
  const confirmOkDialogRef = useRef(confirmOkDialog);

  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pongTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPongRef = useRef<number>(Date.now());

  const isPageVisibleRef = useRef(true);
  const reconnectOnVisibleRef = useRef(true);

  const connectWithRetryRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    handleResetStreamRef.current = handleResetStream;
    setSessionStreamIdRef.current = setSessionStreamId;
    audioStreamRef.current = audioStream;
    confirmOkDialogRef.current = confirmOkDialog;
  }, [handleResetStream, setSessionStreamId, audioStream, confirmOkDialog]);

  const isConnectingRef = useRef(false);
  const retryCountRef = useRef(0);

  const [connectionStatus, setConnectionStatus] = useState<
    'disconnected' | 'connecting' | 'connected' | 'error'
  >('disconnected');

  const clearPingPongTimers = useCallback(() => {
    if (pingIntervalRef.current) {
      console.debug('Clearing ping interval');
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pongTimeoutRef.current) {
      console.debug('Clearing pong timeout');
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const startPingPong = useCallback(() => {
    clearPingPongTimers();

    console.debug('Starting ping-pong mechanism');
    pingIntervalRef.current = setInterval(() => {
      console.debug('Sending ping...');
      if (
        wsRef.current?.readyState === WebSocket.OPEN
        // && isPageVisibleRef.current
      ) {
        const pingMessage = {
          type: 'ping',
          timestamp: Date.now(),
        };

        try {
          wsRef.current.send(JSON.stringify(pingMessage));

          pongTimeoutRef.current = setTimeout(() => {
            console.debug('Pong timeout - reconnecting...');
            if (playMessageEnabled && connectWithRetryRef.current) {
              connectWithRetryRef.current();
            }
          }, pongTimeOut);
        } catch (error) {
          console.error('Error sending ping:', error);
        }
      }
    }, pingInterval);
  }, [clearPingPongTimers, playMessageEnabled]);

  const handlePongMessage = useCallback(() => {
    lastPongRef.current = Date.now();
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const hasUserActivation = () => {
    return !!(navigator as any).userActivation?.hasBeenActive;
  };

  const disconnect = useCallback(() => {
    retryCountRef.current = maxRetry;
    clearPingPongTimers();

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }

    setSessionStreamIdRef.current(undefined);
    setConnectionStatus('disconnected');
    isConnectingRef.current = false;
  }, [clearPingPongTimers]);

  const connectWebSocket = useCallback(() => {
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      isConnectingRef.current
    ) {
      console.debug('Start ping/pong (WebSocket already connected)');
      startPingPong();
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      console.debug('New ws connecting....');
      isConnectingRef.current = true;
      setConnectionStatus('connecting');
      const host = env.REACT_APP_MIMI_TTS_WS_URL;
      const wsUrl = `${host}?sessionId=${encodeURIComponent(wsSessionId)}`;

      try {
        wsRef.current = new WebSocket(wsUrl);
        wsRef.current.binaryType = 'arraybuffer';

        const connectionTimeout = setTimeout(() => {
          if (
            wsRef.current &&
            wsRef.current.readyState === WebSocket.CONNECTING
          ) {
            console.error('WebSocket connection timeout');
            wsRef.current.close();
            setConnectionStatus('error');
            isConnectingRef.current = false;
            reject(new Error('Connection timeout'));
          }
        }, 5000);

        wsRef.current.onerror = (error) => {
          console.debug('WebSocket error:', error);
          clearTimeout(connectionTimeout);
          clearPingPongTimers();
          setConnectionStatus('error');
          isConnectingRef.current = false;
          wsRef.current = null;
          reject(error);
        };

        wsRef.current.onopen = () => {
          console.debug('WebSocket connected');
          clearTimeout(connectionTimeout);
          setConnectionStatus('connected');
          isConnectingRef.current = false;
          lastPongRef.current = Date.now();
          startPingPong();
          resolve();
        };

        wsRef.current.onmessage = async (event) => {
          try {
            const messageData = JSON.parse(event.data);
            const { type, data, isLastChunk, chunkIndex, sessionId } =
              messageData;

            if (type === 'pong') {
              handlePongMessage();
              return;
            }

            //if socket message comming before api response
            if (chunkIndex === 0 && sessionStreamIdRef.current === undefined) {
              sessionStreamIdRef.current = sessionId;
            }
            if (sessionStreamIdRef.current !== sessionId) {
              return;
            }
            if (type !== 'chunk') return;
            // Decode base64
            const binaryString = window.atob(data);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            if (bytes.length > 0) {
              await audioStreamRef.current.playChunk(
                bytes,
                chunkIndex,
                isLastChunk,
                () => {
                  if (isLastChunk) {
                    setSessionStreamIdRef.current(undefined);
                  }
                }
              );
            }
          } catch (error) {
            handleResetStreamRef.current();
            console.error('Error processing WebSocket message:', error);
          }
        };

        wsRef.current.onclose = (ev) => {
          console.debug(
            'WebSocket disconnected, code:',
            ev.code,
            'reason:',
            ev.reason
          );
          disconnect();
          clearTimeout(connectionTimeout);
          isConnectingRef.current = false;
          wsRef.current = null;
          retryCountRef.current = 0;
        };
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        setConnectionStatus('error');
        isConnectingRef.current = false;
        reject(error);
      }
    });
  }, [
    sessionStreamIdRef,
    wsSessionId,
    startPingPong,
    clearPingPongTimers,
    handlePongMessage,
    disconnect,
  ]);

  const connectWithRetry = useCallback(async () => {
    while (retryCountRef.current < maxRetry && playMessageEnabled) {
      try {
        await connectWebSocket();
        retryCountRef.current = 0;
        return;
      } catch (error) {
        retryCountRef.current++;
        console.error(
          `Connection attempt ${retryCountRef.current} failed:`,
          error
        );

        if (retryCountRef.current < maxRetry && playMessageEnabled) {
          await sleep(3000);
        }
      }
    }

    if (retryCountRef.current >= maxRetry) {
      console.error('Max retry attempts reached');
      setConnectionStatus('error');
    }
  }, [playMessageEnabled, connectWebSocket]);

  useEffect(() => {
    connectWithRetryRef.current = connectWithRetry;
  }, [connectWithRetry]);

  // Handle page visibility change
  const handleVisibilityChange = useCallback(() => {
    console.debug('[TTS-stream] handleVisibilityChange', {
      ws: wsRef.current?.readyState,
      reconnectOnVisibleRef: reconnectOnVisibleRef.current,
      isVisible: !document.hidden,
    });
    const isVisible = !document.hidden;
    isPageVisibleRef.current = isVisible;

    if (isVisible) {
      if (reconnectOnVisibleRef.current && playMessageEnabled) {
        reconnectOnVisibleRef.current = false;
        connectWithRetry();
      }
    } else {
      // clearPingPongTimers();
      // Reset stream when page becomes visible
      handleResetStreamRef.current();
      if (connectionStatus === 'connected') {
        reconnectOnVisibleRef.current = true;
      }
    }
  }, [
    playMessageEnabled,
    connectWithRetry,
    // clearPingPongTimers,
    connectionStatus,
  ]);

  const handlePageShow = useCallback(() => {
    console.debug('[TTS-stream] handlePageShow', {
      ws: wsRef.current?.readyState,
      isConnectingRef: isConnectingRef.current,
    });
    isPageVisibleRef.current = true;
    if (
      playMessageEnabled &&
      (connectionStatus === 'disconnected' || reconnectOnVisibleRef.current)
    ) {
      reconnectOnVisibleRef.current = false;
      connectWithRetry();
    }
  }, [playMessageEnabled, connectionStatus, connectWithRetry]);

  const handlePageHide = useCallback(() => {
    console.debug('[TTS-stream] handlePageHide');
    isPageVisibleRef.current = false;
    // clearPingPongTimers();
    if (connectionStatus === 'connected') {
      reconnectOnVisibleRef.current = true;
    }
  }, [/*clearPingPongTimers, */ connectionStatus]);
  const handleFocus = useCallback(() => {
    console.debug('[TTS-stream] focus', {
      ws: wsRef.current?.readyState,
      isConnectingRef: isConnectingRef.current,
    });
    isPageVisibleRef.current = true;

    if (playMessageEnabled) {
      // Delay for iOS restore network
      setTimeout(() => {
        if (
          wsRef.current?.readyState !== WebSocket.OPEN &&
          !isConnectingRef.current
        ) {
          reconnectOnVisibleRef.current = false;
          connectWithRetry();
        }
      }, 500);

      setTimeout(() => {
        if (
          wsRef.current?.readyState !== WebSocket.OPEN &&
          !isConnectingRef.current
        ) {
          reconnectOnVisibleRef.current = false;
          connectWithRetry();
        }
      }, 2000);
    }
  }, [playMessageEnabled, connectWithRetry]);

  const handleBlur = useCallback(
    () => {
      console.debug('[TTS-stream] handleBur');
      isPageVisibleRef.current = false;
      reconnectOnVisibleRef.current = true;
      // clearPingPongTimers();
    },
    [
      /*clearPingPongTimers*/
    ]
  );

  useEffect(() => {
    // IOS Safari specific events
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [
    handleVisibilityChange,
    handlePageShow,
    handlePageHide,
    handleFocus,
    handleBlur,
  ]);

  useEffect(() => {
    if (playMessageEnabled) {
      retryCountRef.current = 0;
      // 開始直後はWebsocketが混雑していて勝手に切断されることがあるため、少し待ってから接続を試みる
      sleep(1000).then(() => {
        connectWithRetry();
      });
    } else {
      disconnect();
    }
    return () => {
      clearPingPongTimers();
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
    };
  }, [connectWithRetry, disconnect, playMessageEnabled, clearPingPongTimers]);

  // 接続が完了するまで待つ。音声伝達への切替直後など再接続中に読み上げ要求された場合に使う
  const waitForConnection = useCallback(
    async (timeoutMs: number = connectionWaitTimeout) => {
      const startedAt = Date.now();
      while (wsRef.current?.readyState !== WebSocket.OPEN) {
        if (Date.now() - startedAt >= timeoutMs) {
          throw new TtsSocketNotConnectedError();
        }
        await sleep(50);
      }
    },
    []
  );

  return {
    connectWebSocket,
    disconnect,
    isConnected: () => connectionStatus === 'connected',
    connectionStatus,
    audioStream,
    hasUserActivation,
    waitForConnection,
    sendRequest: (params: {
      type: TtsService;
      text: string;
      lang: string;
      format: string; // 'WAV' or 'MP3',
      rate?: number;
    }) => {
      // とりあえず直書き

      // sessionIdはクライアントが自分で決めてサーバーに送る方式にする。
      const sessionId = `session_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 9)}`;

      const data = {
        type:
          params.type === TtsService.Feat
            ? 'tts-ft'
            : params.type === TtsService.Azure
            ? 'tts-az'
            : '',
        text: params.text,
        lang: params.lang,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        audio_format: params.format,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        session_id: sessionId,
        rate: params.rate ?? 1,
      };

      // 未接続のまま送ると要求が捨てられ、届かない音声を待ち続けてしまうため例外にする
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        throw new TtsSocketNotConnectedError();
      }
      wsRef.current.send(JSON.stringify(data));
      return sessionId;
    },
  };
};
